import type {
	FieldPath,
	FieldValues,
	UseControllerProps,
} from 'react-hook-form'

import React from 'react'
import { CircleHelpIcon } from 'lucide-react'
import { useFormContext } from 'react-hook-form'

import {
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/shared/components/ui/form'
import { Input } from '@/shared/components/ui/input'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { cn } from '@/shared/lib/utils'

export default function RHFFileInput<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
	name,
	label,
	hint,
	description,
	defaultValue,
	shouldUnregister,
	className,
	...other
}: UseControllerProps<TFieldValues, TName> &
	React.ComponentProps<typeof Input> & {
		label: string
		hint?: string
		description?: string
		required?: boolean
	}) {
	const { control } = useFormContext()

	return (
		<FormField
			name={name}
			defaultValue={defaultValue}
			shouldUnregister={shouldUnregister}
			control={control}
			render={({ field, fieldState: { error } }) => (
				<FormItem>
					<FormLabel>
						{label}
						{hint && (
							<Tooltip>
								<TooltipTrigger asChild>
									<CircleHelpIcon className='text-muted-foreground' />
								</TooltipTrigger>
								<TooltipContent side='top'>{hint}</TooltipContent>
							</Tooltip>
						)}
					</FormLabel>
					<FormControl>
						<Input
							onChange={(e) => {
								const files = []
								for (let i = 0; i < (e.target.files?.length || 0); i++) {
									const file = e.target.files?.[i] ?? null
									if (file) {
										files.push(file)
									}
								}
								field.onChange(files)
							}}
							multiple
							type='file'
							name={name}
							invalid={!!error}
							className={cn(
								'visible relative inset-auto w-full py-1 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground',
								className
							)}
							{...other}
						/>
					</FormControl>
					{!!description && <FormDescription>{description}</FormDescription>}
					<FormMessage />
				</FormItem>
			)}
		/>
	)
}
